
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

// ==================== Model ====================
export interface TaskLink {
  id: string;
  taskId: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  createdAt?: string;
  createdBy?: string;
}

// ==================== Async Thunks ====================

// 📥 جلب الروابط حسب المهمة
export const getTaskLinksByTaskId = createAsyncThunk(
  'TaskLink/getByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskLinksByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ➕ إضافة رابط جديد
export const addTaskLink = createAsyncThunk(
  'TaskLink/add',
  async (item: Omit<TaskLink, 'id' | 'createdAt' | 'createdBy' | 'entityName'>, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskLink', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة الرابط');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ✏️ تحديث رابط
export const updateTaskLink = createAsyncThunk(
  'TaskLink/update',
  async (item: TaskLink, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskLink', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تعديل الرابط');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🗑️ حذف رابط
export const deleteTaskLink = createAsyncThunk(
  'TaskLink/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskLink?id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في حذف الرابط');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Slice ====================

interface TaskLinkState {
  taskLinkList: TaskLink[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskLinkState = {
  taskLinkList: [],
  loading: false,
  error: null
};

const TaskLinkSlice = createSlice({
  name: 'TaskLink',
  initialState,
  reducers: {
    clearTaskLinks: state => {
      state.taskLinkList = [];
      state.error = null;
    },
    addLocalTaskLink: (state, action: PayloadAction<TaskLink>) => {
      state.taskLinkList.unshift(action.payload);
    },
    updateLocalTaskLink: (state, action: PayloadAction<TaskLink>) => {
      const index = state.taskLinkList.findIndex(l => l.id === action.payload.id);
      if (index !== -1) state.taskLinkList[index] = action.payload;
    },
    removeLocalTaskLink: (state, action: PayloadAction<string>) => {
      state.taskLinkList = state.taskLinkList.filter(l => l.id !== action.payload);
    }
  },
  extraReducers: builder => {
    builder
      // 🔄 Get
      .addCase(getTaskLinksByTaskId.pending, state => {
        state.loading = true;
      })
      .addCase(getTaskLinksByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskLinkList = action.payload;
      })
      .addCase(getTaskLinksByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ➕ Add
      .addCase(addTaskLink.fulfilled, (state, action) => {
        state.taskLinkList.unshift(action.payload);
      })

      // ✏️ Update
      .addCase(updateTaskLink.fulfilled, (state, action) => {
        const index = state.taskLinkList.findIndex(l => l.id === action.payload.id);
        if (index !== -1) state.taskLinkList[index] = action.payload;
      })

      // 🗑️ Delete
      .addCase(deleteTaskLink.fulfilled, (state, action) => {
        state.taskLinkList = state.taskLinkList.filter(l => l.id !== action.payload);
      });
  }
});

export const { clearTaskLinks, addLocalTaskLink, updateLocalTaskLink, removeLocalTaskLink } =
  TaskLinkSlice.actions;

export default TaskLinkSlice.reducer;
